import { useEffect, useRef, useState } from 'react'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded'
import FlagRoundedIcon from '@mui/icons-material/FlagRounded'
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded'
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded'
import { Card, Button, NumberStepper, TextInput, Toggle } from '../../Common'
import {
  DEFAULT_OWNER_SETTINGS,
  normalizeOwnerSettings,
  type OwnerSettings,
} from '../../../services/ownerConsoleSettingsModel'
import { saveOwnerSettings, subscribeOwnerSettings } from '../../../services/ownerConsoleService'
import { SlackNotificationsPanel } from './SlackNotificationsPanel'
import { useNls } from '../../../nls/nlsProvider'
import { navigateOwner } from '../ownerRouter'

type OwnerSettingsPanelProps = {
  actorUid: string
}


export const OwnerSettingsPanel = ({ actorUid }: OwnerSettingsPanelProps) => {
  const { t } = useNls()
  const [draft, setDraft] = useState<OwnerSettings>(DEFAULT_OWNER_SETTINGS)
  const [loaded, setLoaded] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ tone: 'good' | 'warn' | 'danger'; text: string } | null>(null)
  const dirtyRef = useRef(false)

  useEffect(() => {
    dirtyRef.current = dirty
  }, [dirty])

  useEffect(() => subscribeOwnerSettings((settings) => {
    setLoaded(true)
    if (!dirtyRef.current) {
      setDraft(settings)
      setMessage(null)
    }
  }, (error) => {
    setLoaded(true)
    setMessage({ tone: 'danger', text: error instanceof Error ? error.message : 'Owner settings could not be loaded.' })
  }), [])

  const updateDraft = (next: OwnerSettings) => {
    setDraft(normalizeOwnerSettings(next))
    setDirty(true)
    setMessage(null)
  }

  const save = async () => {
    setSaving(true)
    setMessage(null)
    try {
      await saveOwnerSettings(draft, actorUid)
      setDirty(false)
      setMessage({ tone: 'good', text: 'Owner settings saved.' })
    } catch (error) {
      setMessage({ tone: 'danger', text: error instanceof Error ? error.message : 'Owner settings could not be saved.' })
    } finally {
      setSaving(false)
    }
  }

  const disabled = saving || !loaded

  return (
    <section className="ownerPanel ownerSettingsPanel" aria-label="Owner settings">
      <div className="ownerPanelHeader">
        <div>
          <span className="ownerEyebrow">Owner settings</span>
          <h2>Persistent platform configuration</h2>
          <p>Global settings that remain in effect between owner commands. One-shot and destructive operations stay in Controls.</p>
        </div>
        <div className="ownerSettingsActions">
          <Button
            variant="tertiary"
            size="sm"
            icon={<RestartAltRoundedIcon fontSize="inherit" />}
            disabled={disabled}
            onClick={() => updateDraft(DEFAULT_OWNER_SETTINGS)}
          >
            Restore defaults
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<SaveRoundedIcon fontSize="inherit" />}
            loading={saving}
            disabled={!loaded || !dirty}
            onClick={() => void save()}
          >
            Save settings
          </Button>
        </div>
      </div>

      {message ? (
        <div className={`ownerActionResult ownerTone--${message.tone}`} role="status">
          <p>{message.text}</p>
        </div>
      ) : null}

      <div className="ownerSettingsGrid">
        <Card
          variant="outline"
          accent="primary"
          eyebrow="System"
          title="Maintenance and catalog"
          subtitle="Runtime switches with app-wide blast radius."
          icon={<SettingsSuggestRoundedIcon fontSize="inherit" />}
        >
          <div className="ownerSettingsRows">
            <label className="ownerSettingsRow">
              <span>
                <strong>Maintenance mode</strong>
                <small>Blocks domain writes and shows the global banner copy.</small>
              </span>
              <Toggle
                checked={draft.maintenanceMode.enabled}
                disabled={disabled}
                onChange={(enabled) => updateDraft({ ...draft, maintenanceMode: { ...draft.maintenanceMode, enabled } })}
              />
            </label>
            <label className="ownerSettingsRow ownerSettingsRow--stacked">
              <span>
                <strong>Banner message</strong>
                <small>Short operator-facing message for the maintenance banner.</small>
              </span>
              <textarea
                value={draft.maintenanceMode.message}
                placeholder="Brief maintenance notice"
                disabled={disabled}
                onChange={(event) => updateDraft({ ...draft, maintenanceMode: { ...draft.maintenanceMode, message: event.target.value } })}
              />
            </label>
            <label className="ownerSettingsRow">
              <span>
                <strong>Public catalog freeze</strong>
                <small>Prevent catalog/listing churn while reviewing public surfaces.</small>
              </span>
              <Toggle
                checked={draft.publicCatalogFreeze}
                disabled={disabled}
                onChange={(publicCatalogFreeze) => updateDraft({ ...draft, publicCatalogFreeze })}
              />
            </label>
            <div className="ownerSettingsRow">
              <span>
                <strong>{t('NLSOwnerConsoleSettingsFlagsHeading')}</strong>
                <small>{t('NLSOwnerConsoleSettingsFlagsDescription')}</small>
              </span>
              <Button
                variant="tertiary"
                size="sm"
                icon={<FlagRoundedIcon fontSize="inherit" />}
                onClick={() => navigateOwner({ view: 'config-flags' })}
              >
                {t('NLSOwnerConsoleSettingsFlagsPointer')}
              </Button>
            </div>
          </div>
        </Card>

        <Card
          variant="outline"
          accent="warn"
          eyebrow="Retention"
          title="Debug and owner access"
          subtitle="Console lease, diagnostic retention, and escalation inbox."
          icon={<ShieldRoundedIcon fontSize="inherit" />}
        >
          <div className="ownerSettingsRows">
            <label className="ownerSettingsRow">
              <span>
                <strong>Debug-session retention</strong>
                <small>Days retained before diagnostic sessions age out.</small>
              </span>
              <NumberStepper
                value={draft.debugSessionRetentionDays}
                min={1}
                max={365}
                suffix="d"
                size="sm"
                variant="orbit"
                disabled={disabled}
                ariaLabel="Debug session retention days"
                onChange={(debugSessionRetentionDays) => updateDraft({ ...draft, debugSessionRetentionDays })}
              />
            </label>
            <label className="ownerSettingsRow">
              <span>
                <strong>Owner session lease</strong>
                <small>Minutes before the console should ask for a fresh owner session.</small>
              </span>
              <NumberStepper
                value={draft.ownerConsoleLeaseMinutes}
                min={5}
                max={240}
                step={5}
                suffix="m"
                size="sm"
                variant="orbit"
                disabled={disabled}
                ariaLabel="Owner console session lease minutes"
                onChange={(ownerConsoleLeaseMinutes) => updateDraft({ ...draft, ownerConsoleLeaseMinutes })}
              />
            </label>
            <label className="ownerSettingsRow ownerSettingsRow--stacked">
              <span>
                <strong>Owner alert email</strong>
                <small>Primary inbox for system and moderation alerts.</small>
              </span>
              <TextInput
                value={draft.ownerAlertEmail}
                size="sm"
                type="email"
                disabled={disabled}
                leadingIcon={<CampaignRoundedIcon fontSize="inherit" />}
                placeholder="owner@example.com"
                onChange={(ownerAlertEmail) => updateDraft({ ...draft, ownerAlertEmail })}
              />
            </label>
          </div>
        </Card>

        <Card
          variant="outline"
          accent="success"
          eyebrow="Economy"
          title="Signup and curse defaults"
          subtitle="Global economy baselines for new accounts and AI-generation penalty defaults."
          icon={<AutoAwesomeRoundedIcon fontSize="inherit" />}
        >
          <div className="ownerSettingsRows">
            <label className="ownerSettingsRow">
              <span>
                <strong>Signup bonus</strong>
                <small>Default one-time Cr grant for new accounts.</small>
              </span>
              <NumberStepper
                value={draft.globalSignupBonusCr}
                min={0}
                max={100000}
                step={25}
                suffix="Cr"
                size="sm"
                variant="orbit"
                disabled={disabled}
                ariaLabel="Global signup bonus amount"
                onChange={(globalSignupBonusCr) => updateDraft({ ...draft, globalSignupBonusCr })}
              />
            </label>
            <label className="ownerSettingsRow">
              <span>
                <strong>Daily new-signup cap</strong>
                <small>Safety cap for Cr earned by fresh signups in one day.</small>
              </span>
              <NumberStepper
                value={draft.globalDailyNewSignupCrCap}
                min={0}
                max={100000}
                step={25}
                suffix="Cr"
                size="sm"
                variant="orbit"
                disabled={disabled}
                ariaLabel="Global daily Cr cap for new signups"
                onChange={(globalDailyNewSignupCrCap) => updateDraft({ ...draft, globalDailyNewSignupCrCap })}
              />
            </label>
            <label className="ownerSettingsRow">
              <span>
                <strong>AI-generation curse defaults</strong>
                <small>Default penalty mode for repeated failed generation loops.</small>
              </span>
              <Toggle
                checked={draft.globalAiGenerationCurseDefaults.enabled}
                disabled={disabled}
                onChange={(enabled) => updateDraft({
                  ...draft,
                  globalAiGenerationCurseDefaults: { ...draft.globalAiGenerationCurseDefaults, enabled },
                })}
              />
            </label>
            <label className="ownerSettingsRow">
              <span>
                <strong>Curse duration</strong>
                <small>Default hours before an AI-generation curse expires.</small>
              </span>
              <NumberStepper
                value={draft.globalAiGenerationCurseDefaults.durationHours}
                min={1}
                max={168}
                suffix="h"
                size="sm"
                variant="orbit"
                disabled={disabled}
                ariaLabel="Global AI generation curse duration hours"
                onChange={(durationHours) => updateDraft({
                  ...draft,
                  globalAiGenerationCurseDefaults: { ...draft.globalAiGenerationCurseDefaults, durationHours },
                })}
              />
            </label>
            <label className="ownerSettingsRow">
              <span>
                <strong>Break cost</strong>
                <small>Default Cr payment to clear the curse early.</small>
              </span>
              <NumberStepper
                value={draft.globalAiGenerationCurseDefaults.breakCostCr}
                min={0}
                max={100000}
                step={25}
                suffix="Cr"
                size="sm"
                variant="orbit"
                disabled={disabled}
                ariaLabel="Global AI generation curse break cost"
                onChange={(breakCostCr) => updateDraft({
                  ...draft,
                  globalAiGenerationCurseDefaults: { ...draft.globalAiGenerationCurseDefaults, breakCostCr },
                })}
              />
            </label>
          </div>
        </Card>
        <SlackNotificationsPanel actorUid={actorUid} />
      </div>
    </section>
  )
}
